import { create } from "zustand";
import * as api from "../api/reports";
import type { RunReportParams } from "../api/reports";
import type { Report, ReportRunResult, Visibility } from "../api/types";
import { useChat } from "./chat";

interface ReportsState {
  list: Report[];
  current: Report | null;
  result: ReportRunResult | null;
  loadingList: boolean;
  loadingDetail: boolean;
  loadingRun: boolean;
}

interface ReportsActions {
  loadList: () => Promise<void>;
  loadDetail: (id: number) => Promise<void>;
  run: (id: number, params?: RunReportParams) => Promise<void>;
  save: (id: number, messageId: number, expectedVersion: number) => Promise<void>;
  rename: (id: number, name: string) => Promise<void>;
  remove: (id: number) => Promise<void>;
  createFromMessage: (messageId: number, name: string) => Promise<Report>;
  setVisibility: (id: number, visibility: Visibility) => Promise<void>;
  setFavorite: (id: number, favorite: boolean) => Promise<void>;
  duplicate: (id: number) => Promise<Report>;
  reset: () => void;
}

type Store = ReportsState & ReportsActions;

const INITIAL: ReportsState = {
  list: [],
  current: null,
  result: null,
  loadingList: false,
  loadingDetail: false,
  loadingRun: false,
};

let runController: AbortController | null = null;

export const useReports = create<Store>((set) => ({
  ...INITIAL,

  async loadList() {
    set({ loadingList: true });
    try {
      const list = await api.listReports();
      set({ list, loadingList: false });
    } catch (e) {
      set({ loadingList: false });
      throw e;
    }
  },

  async loadDetail(id) {
    runController?.abort();
    runController = null;
    set({ loadingDetail: true, current: null, result: null });
    try {
      const current = await api.getReport(id);
      set({ current, loadingDetail: false });
    } catch (e) {
      set({ loadingDetail: false });
      throw e;
    }
  },

  async run(id, params) {
    runController?.abort();
    const controller = new AbortController();
    runController = controller;
    set({ loadingRun: true });
    try {
      const result = await api.runReport(id, params, controller.signal);
      if (runController === controller) {
        set({ result, loadingRun: false });
      }
    } catch (e) {
      if (controller.signal.aborted) return;
      if (runController === controller) set({ loadingRun: false });
      throw e;
    } finally {
      if (runController === controller) runController = null;
    }
  },

  async save(id, messageId, expectedVersion) {
    const current = await api.saveReport(id, messageId, expectedVersion);
    set({ current });
  },

  async rename(id, name) {
    const updated = await api.renameReport(id, name);
    set((s) => ({
      current: s.current?.id === id ? updated : s.current,
      list: s.list.map((r) =>
        r.id === id ? { ...updated, is_favorite: r.is_favorite } : r,
      ),
    }));
  },

  async remove(id) {
    // Optimistic local update so the UI reflects the deletion before the
    // network round-trip resolves.
    set((s) => ({
      list: s.list.filter((r) => r.id !== id),
      current: s.current?.id === id ? null : s.current,
      result: s.current?.id === id ? null : s.result,
    }));
    // The server cascade-deletes this report's editor chats. Any chat state
    // pointing at one of them is now stale — clear the active editor session
    // defensively (the chat list itself never holds editor chats).
    useChat.setState({ activeSessionId: null, messages: [] });

    try {
      await api.deleteReport(id);
    } catch (e) {
      // Server rejected the delete — pull truth back from the server so the UI
      // doesn't end up in a partially-cleared state.
      try {
        await useReports.getState().loadList();
      } catch {
        /* ignore secondary failure */
      }
      throw e;
    }
  },

  async createFromMessage(messageId, name) {
    const created = await api.createReportFromMessage(messageId, name);
    set((s) => ({ list: [created, ...s.list] }));
    return created;
  },

  async setVisibility(id, visibility) {
    const updated = await api.setReportVisibility(id, visibility);
    // Single-report responses don't know about the caller's star — keep the
    // flag from the list entry being replaced.
    set((s) => ({
      current: s.current?.id === id ? updated : s.current,
      list: s.list.map((r) =>
        r.id === id ? { ...updated, is_favorite: r.is_favorite } : r,
      ),
    }));
  },

  async setFavorite(id, favorite) {
    // Optimistic: the star flips instantly, the section membership follows.
    set((s) => ({
      list: s.list.map((r) => (r.id === id ? { ...r, is_favorite: favorite } : r)),
    }));
    try {
      await api.setReportFavorite(id, favorite);
    } catch (e) {
      set((s) => ({
        list: s.list.map((r) =>
          r.id === id ? { ...r, is_favorite: !favorite } : r,
        ),
      }));
      throw e;
    }
  },

  async duplicate(id) {
    const created = await api.duplicateReport(id);
    set((s) => ({ list: [created, ...s.list] }));
    return created;
  },

  reset() {
    runController?.abort();
    runController = null;
    set({ ...INITIAL });
  },
}));
